import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const ImageProcessorConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['operation', 'settings']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const operations = [
    { value: 'resize', label: 'Átméretezés', icon: '📐', desc: 'Kép méretének változtatása' },
    { value: 'crop', label: 'Vágás', icon: '✂️', desc: 'Kép kivágása' },
    { value: 'rotate', label: 'Forgatás', icon: '🔄', desc: 'Kép elforgatása' },
    { value: 'compress', label: 'Tömörítés', icon: '🗜️', desc: 'Fájlméret csökkentése' },
    { value: 'convert', label: 'Konvertálás', icon: '🔀', desc: 'Formátum váltás' },
    { value: 'filter', label: 'Szűrő', icon: '🎨', desc: 'Vizuális effektek' },
    { value: 'watermark', label: 'Vízjel', icon: '©️', desc: 'Vízjel hozzáadása' },
  ];

  const filters = [
    { value: 'grayscale', label: 'Szürkeárnyalatos' },
    { value: 'sepia', label: 'Szépia' },
    { value: 'blur', label: 'Elmosás' },
    { value: 'sharpen', label: 'Élesítés' },
    { value: 'brightness', label: 'Fényerő' },
    { value: 'contrast', label: 'Kontraszt' },
  ];

  return (
    <div className="space-y-3">
      {/* Művelet típus */}
      <CollapsibleSection
        id="operation"
        title="Művelet Típusa"
        icon="🖼️"
        color="purple"
        isExpanded={expandedSections.has('operation')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            {operations.map((op) => (
              <button
                key={op.value}
                onClick={() => updateConfig('operation', op.value)}
                className={`flex items-center p-3 rounded-md border text-sm transition-all ${
                  (node.data.config.operation || 'resize') === op.value
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                }`}
              >
                <span className="text-xl mr-3">{op.icon}</span>
                <div className="flex-1 text-left">
                  <div className="font-medium">{op.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{op.desc}</div>
                </div>
                {(node.data.config.operation || 'resize') === op.value && (
                  <span className="text-purple-500">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Beállítások */}
      <CollapsibleSection
        id="settings"
        title="Beállítások"
        icon="⚙️"
        color="blue"
        isExpanded={expandedSections.has('settings')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Kép forrás
            </label>
            <input
              type="text"
              value={node.data.config.image || ''}
              onChange={(e) => updateConfig('image', e.target.value)}
              className={commonStyles.input}
              placeholder="{{image_url}} vagy /path/to/image.jpg"
            />
          </div>

          {/* Átméretezés */}
          {(node.data.config.operation === 'resize' || !node.data.config.operation) && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={commonStyles.smallLabel}>Szélesség (px)</label>
                <input
                  type="number"
                  value={node.data.config.width || 800}
                  onChange={(e) => updateConfig('width', parseInt(e.target.value))}
                  className={commonStyles.input}
                  min="1"
                />
              </div>
              <div>
                <label className={commonStyles.smallLabel}>Magasság (px)</label>
                <input
                  type="number"
                  value={node.data.config.height || 600}
                  onChange={(e) => updateConfig('height', parseInt(e.target.value))}
                  className={commonStyles.input}
                  min="1"
                />
              </div>
            </div>
          )}

          {/* Forgatás */}
          {node.data.config.operation === 'rotate' && (
            <div>
              <label className={commonStyles.label}>
                Forgatás szöge (fok)
              </label>
              <input
                type="number"
                value={node.data.config.angle || 90}
                onChange={(e) => updateConfig('angle', parseInt(e.target.value))}
                className={commonStyles.input}
                placeholder="90"
                step="90"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                90, 180, 270 vagy egyedi érték
              </p>
            </div>
          )}

          {/* Tömörítés */}
          {node.data.config.operation === 'compress' && (
            <div>
              <label className={commonStyles.label}>
                Minőség (%)
              </label>
              <input
                type="range"
                value={node.data.config.quality || 80}
                onChange={(e) => updateConfig('quality', parseInt(e.target.value))}
                className="w-full"
                min="1"
                max="100"
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {node.data.config.quality || 80}%
              </div>
            </div>
          )}

          {/* Konvertálás */}
          {node.data.config.operation === 'convert' && (
            <div>
              <label className={commonStyles.label}>
                Cél formátum
              </label>
              <select
                value={node.data.config.format || 'jpg'}
                onChange={(e) => updateConfig('format', e.target.value)}
                className={commonStyles.select}
              >
                <option value="jpg">JPG</option>
                <option value="png">PNG</option>
                <option value="webp">WebP</option>
                <option value="gif">GIF</option>
                <option value="bmp">BMP</option>
              </select>
            </div>
          )}

          {/* Szűrő */}
          {node.data.config.operation === 'filter' && (
            <div>
              <label className={commonStyles.label}>
                Szűrő típus
              </label>
              <select
                value={node.data.config.filterType || 'grayscale'}
                onChange={(e) => updateConfig('filterType', e.target.value)}
                className={commonStyles.select}
              >
                {filters.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Vízjel */}
          {node.data.config.operation === 'watermark' && (
            <>
              <div>
                <label className={commonStyles.label}>
                  Vízjel szöveg
                </label>
                <input
                  type="text"
                  value={node.data.config.watermarkText || ''}
                  onChange={(e) => updateConfig('watermarkText', e.target.value)}
                  className={commonStyles.input}
                  placeholder="© 2024 Company"
                />
              </div>
              <div>
                <label className={commonStyles.label}>
                  Pozíció
                </label>
                <select
                  value={node.data.config.watermarkPosition || 'bottom-right'}
                  onChange={(e) => updateConfig('watermarkPosition', e.target.value)}
                  className={commonStyles.select}
                >
                  <option value="top-left">Bal felső</option>
                  <option value="top-right">Jobb felső</option>
                  <option value="bottom-left">Bal alsó</option>
                  <option value="bottom-right">Jobb alsó</option>
                  <option value="center">Középen</option>
                </select>
              </div>
            </>
          )}
        </div>
      </CollapsibleSection>

      {/* Kimenet Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="emerald"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A feldolgozás után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{processed_image}}', desc: 'Feldolgozott kép URL', example: 'https://storage.../img.jpg' },
              { var: '{{width}}', desc: 'Kép szélesség', example: '800' },
              { var: '{{height}}', desc: 'Kép magasság', example: '600' },
              { var: '{{file_size}}', desc: 'Fájl méret (byte)', example: '52480' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="amber"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🖼️ Thumbnail generálás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Kép feltöltés → Átméretezés (200x200) → Tárolás
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>©️ Vízjel hozzáadás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Kép → Vízjel → Tömörítés → Email
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>🎨 Képszűrő:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Kép → Szürkeárnyalatos → Tárolás
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default ImageProcessorConfig;
